// ViewResultAsRider.jsx
import React, { useState, useMemo, useCallback } from "react";
import axios from "axios";
import "./ViewResultAsRiderScreen.css";
import {
  IonPage,
  IonContent,
  IonButton,
  IonIcon,
  IonModal,
  IonAlert,
  IonAvatar,
  IonNote,
  IonLoading
} from '@ionic/react';
import {
  arrowBack,
  shield,
  copyOutline,
  navigateOutline
} from 'ionicons/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import { API_URL } from '../../index';
import { formatTime, formatDate } from '../../utils/dateTimeUtils';

const ViewResultAsRider = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, trip, originLocation, destinationLocation } = location.state;

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  console.log("Trip:", trip);
  console.log("Origin Location:", originLocation);
  console.log("Destination Location:", destinationLocation);


  // Memoized stop getters to avoid repeated array searches
  const stops = useMemo(() => {
    const orderedStops = trip.ordered_stops || [];
    return {
      origin: orderedStops.find(stop => stop.type === 'origin') || {},
      pickup: orderedStops.find(stop => stop.type === 'pickup') || {},
      dropoff: orderedStops.find(stop => stop.type === 'dropoff') || {},
      destination: orderedStops.find(stop => stop.type === 'destination') || {}
    };
  }, [trip.ordered_stops]);

  // Memoized calculations
  const tripCost = useMemo(() => {
    const { pickup, dropoff } = stops;
    if (pickup.distanceMeters && dropoff.distanceMeters && trip.costPerKM) {
      const distanceFromPickup = dropoff.distanceMeters - pickup.distanceMeters;
      const distanceKm = distanceFromPickup / 1000;
      return (distanceKm * trip.costPerKM).toFixed(2);
    }
    return 'N/A';
  }, [stops, trip.costPerKM]);

  const departureTime = useMemo(() => {
    return stops.origin.arrivalDateTime || 'N/A';
  }, [stops.origin.arrivalDateTime]);

  const tripStats = useMemo(() => {
    const { destination } = stops;
    return {
      distance: destination.distanceMeters ? `${(destination.distanceMeters / 1000).toFixed(1)} km` : 'N/A',
      duration: destination.etaFromOriginSeconds ? `${Math.round(destination.etaFromOriginSeconds / 60)} min` : 'N/A'
    };
  }, [stops.destination]);

  // Optimized booking handler
  const handleBookSeat = useCallback(async () => {
    setIsLoading(true);

    const bookingData = {
      userId: user?.user_id || 0,
      startLocName: originLocation || stops.origin.name || "Origin",
      startLat: stops.origin.latitude || 0,
      startLong: stops.origin.longitude || 0,
      endLocName: destinationLocation || stops.destination.name || "Destination",
      endLat: stops.destination.latitude || 0,
      endLong: stops.destination.longitude || 0,
      arrivalDate: stops.dropoff.arrivalDateTime || stops.destination.arrivalDateTime || "",
      optimized_routes: [trip]
    };

    try {
      const response = await axios.post(`${API_URL}/sendbookingreqtodriver`, bookingData);
      if (response.data.status === "duplicate") {
        setIsDuplicate(true);
      }
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Rider Book Seat Error - ViewResultAsRider: ', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, originLocation, destinationLocation, stops, trip]);

  const handleReturnHome = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  const handleReturnToResults = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleModalClose = useCallback(() => {
    setShowSuccessPopup(false);
  }, []);

  // Reusable stop component
  const StopItem = ({ stop, label, iconClass, address, time, nextStop }) => (
    <div className="route-stop-compact">
      <div className={iconClass}></div>
      <div className="stop-content-compact">
        <span className="stop-label-compact">{label}</span>
        <span className="stop-address-compact">{address}</span>
        <div className="arrival-time-compact">
          <IonNote>{time}</IonNote>
        </div>
      </div>
      <div className="stop-actions">
        {stop.latitude && stop.longitude && (
          <button 
            className="copy-coordinates-btn"
            onClick={() => navigator.clipboard.writeText(`${stop.latitude}, ${stop.longitude}`)}
            title="Copy Coordinates"
          >
            <IonIcon icon={copyOutline} className="copy-icon" />
          </button>
        )}
        {nextStop && stop.latitude && stop.longitude && nextStop.latitude && nextStop.longitude && (
          <button 
            className="maps-navigation-btn"
            onClick={() => {
              const mapsUrl = `https://www.google.com/maps/dir/${stop.latitude},${stop.longitude}/${nextStop.latitude},${nextStop.longitude}`;
              window.open(mapsUrl, '_blank');
            }}
            title="Navigate to next stop"
          >
            <IonIcon icon={navigateOutline} className="maps-icon" />
          </button>
        )}
      </div>
    </div>
  );

  // Format address helper
  const formatAddress = (stop, fallback) => {
    return stop.name || fallback || `(${stop.latitude?.toFixed(4)}, ${stop.longitude?.toFixed(4)})`;
  };

  return (
    <IonPage className="trip-planner-page">
      <IonContent fullscreen className="trip-planner-content">
        <div className="content-container-compact">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div className="user-header">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <IonButton
                  fill="clear"
                  className="back-button"
                  onClick={() => navigate(-1)}
                >
                  <IonIcon icon={arrowBack} />
                </IonButton>
              </motion.div>
            </div>

            {/* Page Title */}
            <div className="page-title-section">
              <h2 className="page-title">Trip Details</h2>
            </div>

            <div className="date-display">{formatDate(departureTime)}</div>

            {/* Driver Card */}
            <div className="driver-card">
              <div className="driver-info-compact">
                <IonAvatar className="driver-avatar-compact">
                  <img
                    src="/default-avatar.png"
                    alt="Driver"
                    crossOrigin="anonymous"
                  />
                </IonAvatar>
                <div className="driver-details-compact">
                  <span className="driver-name-compact">{trip.driver_name}</span>
                  <IonIcon icon={shield} className="shield-icon-compact" />
                  <span className="driver-tier-compact">Verified</span>
                </div>
              </div>
            </div>

            {/* Price Card */}
            <div className="price-card">
              <div className="price-details">
                <span className="price-label">Price</span>
                <span className="price-value">${tripCost}</span>
              </div>
            </div>

            {/* Route Card */}
            <div className="route-card">
              <div className="route-details-compact">
                <StopItem
                  stop={stops.origin}
                  label="Start"
                  iconClass="origin-circle"
                  address={formatAddress(stops.origin, originLocation)}
                  time={`Depart by: ${formatTime(stops.origin.arrivalDateTime)}`}
                  nextStop={stops.pickup}
                />
                
                <div className="vertical-line"></div>

                <StopItem
                  stop={stops.pickup}
                  label="Pickup"
                  iconClass="pickup-circle"
                  address={formatAddress(stops.pickup)}
                  time={`Arrive by: ${formatTime(stops.pickup.arrivalDateTime)}`}
                  nextStop={stops.dropoff}
                />
                
                <div className="vertical-line"></div>

                <StopItem
                  stop={stops.dropoff}
                  label="Drop-off"
                  iconClass="dropoff-triangle"
                  address={formatAddress(stops.dropoff)}
                  time={`Arrive by: ${formatTime(stops.dropoff.arrivalDateTime)}`}
                  nextStop={stops.destination}
                />
                
                <div className="vertical-line"></div>

                <StopItem
                  stop={stops.destination}
                  label="Destination"
                  iconClass="destination-circle"
                  address={formatAddress(stops.destination, destinationLocation)}
                  time={`Arrive by: ${formatTime(stops.destination.arrivalDateTime)}`}
                />
              </div>
            </div>

            {/* Trip Statistics Card */}
            <div className="stats-card">
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Distance</span>
                  <span className="stat-value">{tripStats.distance}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Duration</span>
                  <span className="stat-value">{tripStats.duration}</span>
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <IonButton
                expand="block"
                className="search-trip-button"
                onClick={() => setShowBookingModal(true)}
              >
                CONTINUE TO BOOK
              </IonButton>
            </motion.div>

            {/* Booking Confirmation Modal */}
            <IonModal isOpen={showBookingModal} onDidDismiss={() => setShowBookingModal(false)}>
              <div className="booking-confirmation-modal-compact">
                <div className="modal-content-section-compact">
                  Are you sure you want to request to join this trip?
                </div>
                <div className="modal-actions-section-compact">
                  <IonButton
                    className="search-trip-button"
                    onClick={() => {
                      handleBookSeat();
                      setShowBookingModal(false);
                    }}
                  >
                    YES, BOOK NOW
                  </IonButton>
                  <IonButton
                    className="cancel-button-viewresult"
                    onClick={() => setShowBookingModal(false)}
                  >
                    CANCEL
                  </IonButton>
                </div>
              </div>
            </IonModal>

            {/* Success / duplicate alert */}
            <IonAlert
              isOpen={showSuccessPopup}
              onDidDismiss={handleModalClose}
              header={isDuplicate ? 'Duplicate Request' : 'Request Details'}
              message={
                isDuplicate ?
                  'You have already booked a seat request for this trip.' :
                  'The driver has been notified of your request. You will receive a notification if the driver accepts.'
              }
              buttons={[
                {
                  text: isDuplicate ? 'Return to Results' : 'Return Home',
                  handler: isDuplicate ? handleReturnToResults : handleReturnHome,
                  cssClass: isDuplicate ? 'return-to-results-button' : '',
                  style: isDuplicate ? { color: '#ff1493', fontWeight: '600' } : {}
                }
              ]}
            />

            {/* Loading screen for booking */}
            <IonLoading
              isOpen={isLoading}
              message="Booking your seat..."
              spinner="crescent"
            />
          </motion.div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ViewResultAsRider;